import { createApi } from '@reduxjs/toolkit/query/react'
import axiosBaseQuery from 'services/axiosBaseQuery'
import { GenericResponse, TableRequestV2, TableResponseV2 } from 'services/types'
import { EntityCreateRequest, ExportRequest, MutationResponse } from './types'
import { sendExportNotification, sendImportNotification } from 'utils'
import { reportsApi } from 'services/ReportManagement/reports'
import { sendNotification } from 'components'
import { DropdownOptionTemplate } from 'types/common'
import { BaseColorType } from '@evrekadev/evreka-ui-components'

export const passApi = createApi({
  reducerPath: 'passApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/engagement/'}`,
  }),
  tagTypes: ['PassList', 'PassTypes', 'PassDetail'],
  endpoints: (builder) => ({
    getPassList: builder.query<
      TableResponseV2<any>,
      TableRequestV2<string> & { entity_id?: string }
    >({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/pass_record/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['PassList'],
    }),
    exportPass: builder.mutation<MutationResponse, ExportRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/pass_record/export/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (error) {}
      },
    }),
    importPasses: builder.mutation<MutationResponse, { client_id: string; body: FormData }>({
      query: ({ client_id, body }) => ({
        url: `clients/${client_id}/pass_record/import/`,
        method: 'POST',
        data: body,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendImportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ImportReports']))
        } catch (error) {}
      },
    }),
    linkUnlinkPassToEntity: builder.mutation<
      MutationResponse,
      {
        client_id: string
        body: { pass_record_id: string; entity_id?: string; action: 'link' | 'unlink' }
      }
    >({
      query: ({ client_id, body }) => ({
        url: `clients/${client_id}/pass_record/link_unlink_entity/`,
        method: 'POST',
        data: body,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (error) {}
      },
      invalidatesTags: ['PassList', 'PassDetail'],
    }),
    createPass: builder.mutation<
      MutationResponse,
      {
        client_id: string
        body: EntityCreateRequest
      }
    >({
      query: ({ client_id, body }) => ({
        url: `clients/${client_id}/pass_record/`,
        method: 'POST',
        data: body,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (error) {}
      },
      invalidatesTags: ['PassList'],
    }),
    updatePass: builder.mutation<
      MutationResponse,
      {
        client_id: string
        pass_record_id: string
        body: Omit<EntityCreateRequest, 'pass_id'>
      }
    >({
      query: ({ client_id, pass_record_id, body }) => ({
        url: `clients/${client_id}/pass_record/${pass_record_id}/`,
        method: 'PUT',
        data: body,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (error) {}
      },
      invalidatesTags: ['PassList', 'PassDetail'],
    }),
    getPassTypes: builder.query<
      GenericResponse & { data: DropdownOptionTemplate<string> },
      { client_id: string }
    >({
      query: ({ client_id }) => ({
        url: `clients/${client_id}/pass_record/pass_type/list/`,
        method: 'GET',
      }),
      providesTags: ['PassTypes'],
    }),
    getPassDetail: builder.query<
      {
        id: string
        pass_id: string
        entity: DropdownOptionTemplate<string>[0] | null
        type: DropdownOptionTemplate<string>[0]
        status?: {
          value: string
          label: string
          color: string
        }
        activation_date?: string
        expiry_date?: string
        whitelist?: unknown[]
        created_at?: string
        updated_at?: string | null
        external_id?: string
      },
      { client_id: string; pass_record_id: string }
    >({
      query: ({ client_id, pass_record_id }) => ({
        url: `clients/${client_id}/pass_record/`,
        method: 'GET',
        params: { pass_record_id },
      }),
      providesTags: ['PassDetail'],
    }),
    searchPass: builder.mutation<
      GenericResponse & { pass_record_list: DropdownOptionTemplate<string> },
      { client_id: string; query_param: string }
    >({
      query: ({ client_id, query_param }) => ({
        url: `clients/${client_id}/pass_record/search/`,
        method: 'POST',
        data: { query_param },
      }),
    }),
    getPassStatuses: builder.query<
      GenericResponse & {
        data: Array<DropdownOptionTemplate<string>[0] & { color: BaseColorType }>
      },
      { client_id: string }
    >({
      query: ({ client_id }) => ({
        url: `clients/${client_id}/pass_record/pass_status/list/`,
        method: 'GET',
      }),
    }),
    changePassStatus: builder.mutation<
      MutationResponse,
      { client_id: string; body: { pass_record_id: string; status_id: string } }
    >({
      query: ({ client_id, body }) => ({
        url: `clients/${client_id}/pass_record/change_status/`,
        method: 'POST',
        data: body,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (error) {}
      },
      invalidatesTags: ['PassList', 'PassDetail'],
    }),
    deletePass: builder.mutation<MutationResponse, { client_id: string; pass_record_id: string }>({
      query: ({ client_id, pass_record_id }) => ({
        url: `clients/${client_id}/pass_record/${pass_record_id}/`,
        method: 'DELETE',
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (error) {}
      },
      invalidatesTags: ['PassList'],
    }),
  }),
})

export const {
  useGetPassListQuery,
  useExportPassMutation,
  useImportPassesMutation,
  useLinkUnlinkPassToEntityMutation,
  useCreatePassMutation,
  useUpdatePassMutation,
  useGetPassTypesQuery,
  useGetPassDetailQuery,
  useSearchPassMutation,
  useGetPassStatusesQuery,
  useChangePassStatusMutation,
  useDeletePassMutation,
} = passApi
